import { getParent, types } from 'mobx-state-tree';
import { api } from 'src/api';
import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';
import { isHttpClientCancel } from 'src/core/services/api/httpClient';
import { createAsync } from 'src/utils/asyncModel';
// Type-only: naming the root for getParent must not create a runtime cycle back to the store index.

/**
 * Where one file has got to on its way up. This is the state of the *upload*, not of the bill —
 * once a file has landed, what happens to it is the bill's own status, read from the bills list so
 * that the modal and the table can never say different things about the same bill.
 */
export const BULK_UPLOAD_STATUS = {
  /** Picked, not yet submitted. The only state in which a file can still be removed. */
  queued: 'queued',
  uploading: 'uploading',
  /** The service created a bill for it. Everything after this is the bill's status. */
  landed: 'landed',
  /** The company already has this file, by content hash. Nothing was created. */
  duplicate: 'duplicate',
  /** Refused before or during the upload. `error` says why, in words meant for the person reading. */
  error: 'error'
};

/** The service refuses a batch larger than this, so the queue will not grow past it either. */
export const MAX_BULK_UPLOAD_FILES = 10;

/**
 * Matches OcrSettings.MaxFileSizeMB in saas-bill-automation-service, which is what the multipart
 * path actually enforces. Turning an oversized file away here saves the round trip that would
 * refuse it anyway.
 */
export const MAX_BULK_UPLOAD_FILE_MB = 10;

/** Mirrors BillFileValidator's allowed extensions. The service is the gate; this is the courtesy. */
export const ACCEPTED_BILL_FILE_TYPES = '.pdf,.png,.jpg,.jpeg,.gif,.webp';

/**
 * Files sent at once. One request per file is what makes each one land, fail, or duplicate on its
 * own; a window keeps ten of them from opening ten multipart requests at the same time.
 */
const UPLOAD_CONCURRENCY = 3;
const SETTLED_STATUSES = [BULK_UPLOAD_STATUS.landed, BULK_UPLOAD_STATUS.duplicate, BULK_UPLOAD_STATUS.error];

/**
 * The File objects themselves, held outside the tree: a snapshot has to stay serialisable, and a
 * File is not. The entry id is the key, so an entry leaving the queue takes its file with it.
 */
const filesById = new Map();
let nextEntryId = 0;
const getCurrentCompanyId = () => localStorage.getItem(USER_SETTINGS_KEYS.company) || '';

/** One file's journey. The file itself lives in {@link filesById} under the same id. */
const BulkUploadEntry = types.model('BulkUploadEntry', {
  id: types.identifier,
  fileName: types.optional(types.string, ''),
  /** Bytes, as the picker reports them, so two pickings of one file compare exactly. */
  fileSizeBytes: types.optional(types.number, 0),
  status: types.optional(types.string, BULK_UPLOAD_STATUS.queued),
  /**
   * Whether the user has asked for this file to go up. A file added while an earlier batch is
   * still uploading must not be swept along with it, so the drain claims only what was submitted.
   */
  isReady: types.optional(types.boolean, false),
  /** Bytes of the request body sent so far, for the progress bar. */
  sentBytes: types.optional(types.number, 0),
  /** The bill this file became, once it landed. Its status is read from the bills list. */
  billId: types.optional(types.string, ''),
  error: types.optional(types.string, '')
}).views(store => ({
  get fileSizeMB() {
    return store.fileSizeBytes / 1048576;
  },
  get sentMB() {
    return store.sentBytes / 1048576;
  },
  /** 0–1. Falls back to nothing sent when the body size is unknown. */
  get progress() {
    if (!store.fileSizeBytes) return 0;
    return Math.min(store.sentBytes / store.fileSizeBytes, 1);
  },
  /** Nothing more will happen to this file without the user picking it again. */
  get isSettled() {
    return SETTLED_STATUSES.includes(store.status);
  },
  /** Only a file not yet submitted can still be taken out. */
  get isEditable() {
    return store.status === BULK_UPLOAD_STATUS.queued && !store.isReady;
  },
  /** Submitted and waiting for a free upload slot. */
  get isReadyToSend() {
    return store.status === BULK_UPLOAD_STATUS.queued && store.isReady;
  }
})).actions(store => ({
  markReady() {
    store.isReady = true;
  },
  markUploading() {
    store.status = BULK_UPLOAD_STATUS.uploading;
    store.error = '';
    store.sentBytes = 0;
  },
  setSentBytes(sentBytes) {
    store.sentBytes = sentBytes;
  },
  markLanded(billId) {
    store.status = BULK_UPLOAD_STATUS.landed;
    store.billId = billId;
  },
  markDuplicate() {
    store.status = BULK_UPLOAD_STATUS.duplicate;
  },
  markError(message) {
    store.status = BULK_UPLOAD_STATUS.error;
    store.error = message;
  }
}));

/**
 * Drains the queue, one request per file, {@link UPLOAD_CONCURRENCY} at a time.
 *
 * Runs on the store rather than in the modal so that closing the modal does not stop it — the PRD
 * asks for uploads that carry on in the background, and a component-local upload cannot do that.
 * A file that fails is recorded against its own entry and never rethrown: one bad file must not
 * take the rest of the batch down with it, and the row's own status is the report, not a toast.
 */
const processQueueAsync = createAsync(() => {
  return async function processQueue(options) {
    const companyId = getCurrentCompanyId();
    if (!companyId) return undefined;
    const uploadOne = async entryId => {
      const file = filesById.get(entryId);
      if (!file) {
        this.failEntry(entryId, 'The file is no longer available. Attach it again.');
        return;
      }
      try {
        var _resp$response;
        const resp = await api.BillAutomationBills.uploadBillsBatch({
          companyId,
          files: [file],
          onUploadProgress: loadedBytes => this.setEntrySentBytes(entryId, loadedBytes)
        }, options);
        const result = resp !== null && resp !== void 0 && resp.isSuccessful ? (_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response[0] : undefined;
        this.applyUploadResult(entryId, result);
      } catch (error) {
        // A cancelled request is the queue being torn down, not a file that failed.
        if (isHttpClientCancel(error)) throw error;
        this.failEntry(entryId, 'Upload failed. Try this file again.');
      }
    };

    // Each worker takes the next submitted entry until none is left, so a slow file holds up only
    // its own slot. Claiming marks the entry, which is what stops two workers taking the same one.
    const worker = async () => {
      for (;;) {
        const entryId = this.claimNextQueuedEntry();
        if (!entryId) return;
        await uploadOne(entryId);
      }
    };

    // More files can be submitted while the last of a round is still going up, and by then every
    // worker may already have found nothing left and stopped. Going round again picks those up;
    // without it they would sit waiting behind a drain that is still marked in progress.
    for (;;) {
      await Promise.all(Array.from({
        length: UPLOAD_CONCURRENCY
      }, worker));
      if (this.readyQueuedCount === 0) break;
    }

    // The list is what every row's status is read from, so it is read once the files are up. The
    // Bills page keeps reading it on its own while anything is still extracting.
    if (this.landedCount > 0) {
      await getParent(this).companyBills.getCompanyBillsAsync.run({}, []);
    }
    return undefined;
  };
});

/**
 * The bulk-upload queue. Deliberately a store rather than component state: the upload modal is
 * closeable while files are still going up, so the queue has to outlive it. Runtime only — a page
 * reload loses it, and nothing here is treated as a persisted record.
 */
const BulkUploadStore = types.model('BulkUploadStore', {
  entries: types.optional(types.array(BulkUploadEntry), []),
  processQueueAsync
}).views(store => ({
  /** Files picked but not yet submitted — what the upload button acts on. */
  get queuedCount() {
    return store.entries.filter(entry => entry.isEditable).length;
  },
  /** Files submitted and still waiting for a slot. */
  get readyQueuedCount() {
    return store.entries.filter(entry => entry.isReadyToSend).length;
  },
  get landedCount() {
    return store.entries.filter(entry => entry.status === BULK_UPLOAD_STATUS.landed).length;
  },
  /** Files neither settled nor still on the picker — what the Bills button counts while shut. */
  get inFlightCount() {
    return store.entries.filter(entry => !entry.isSettled && !entry.isEditable).length;
  },
  get hasEntries() {
    return store.entries.length > 0;
  },
  /** How many more files the queue will take before it hits the service's batch limit. */
  get remainingCapacity() {
    return Math.max(MAX_BULK_UPLOAD_FILES - store.entries.length, 0);
  }
})).actions(store => ({
  /**
   * Takes what the picker gave. A file already queued is ignored, one larger than the service
   * accepts is kept as a failed row rather than dropped silently — being told which file was too
   * big beats a count of how many were — and anything past the batch limit is turned away.
   */
  addFiles(files) {
    const isAlreadyQueued = file => store.entries.some(entry => entry.fileName === file.name && entry.fileSizeBytes === file.size);
    const fresh = files.filter(file => !isAlreadyQueued(file));
    const accepted = fresh.slice(0, store.remainingCapacity);
    let oversized = 0;
    accepted.forEach(file => {
      nextEntryId += 1;
      const id = "bulk-upload-".concat(nextEntryId);
      const isTooLarge = file.size > MAX_BULK_UPLOAD_FILE_MB * 1048576;
      if (isTooLarge) oversized += 1;else filesById.set(id, file);
      store.entries.push({
        id,
        fileName: file.name,
        fileSizeBytes: file.size,
        status: isTooLarge ? BULK_UPLOAD_STATUS.error : BULK_UPLOAD_STATUS.queued,
        error: isTooLarge ? "Larger than ".concat(MAX_BULK_UPLOAD_FILE_MB, " MB") : ''
      });
    });
    return {
      added: accepted.length - oversized,
      oversized,
      rejected: fresh.length - accepted.length
    };
  },
  /** Only a file that has not been sent can be taken out; one in flight is no longer the UI's. */
  removeEntry(entryId) {
    const entry = store.entries.find(item => item.id === entryId);
    if (!entry || !entry.isEditable && !entry.isSettled) return;
    filesById.delete(entryId);
    store.entries.remove(entry);
  },
  /** Marks the next submitted entry as uploading and hands back its id, or nothing when none is left. */
  claimNextQueuedEntry() {
    const entry = store.entries.find(item => item.isReadyToSend);
    if (!entry) return null;
    entry.markUploading();
    return entry.id;
  },
  setEntrySentBytes(entryId, sentBytes) {
    var _store$entries$find;
    (_store$entries$find = store.entries.find(item => item.id === entryId)) === null || _store$entries$find === void 0 || _store$entries$find.setSentBytes(sentBytes);
  },
  applyUploadResult(entryId, result) {
    const entry = store.entries.find(item => item.id === entryId);
    if (!entry) return;
    if (!result) {
      entry.markError('The service did not report an outcome for this file.');
      return;
    }
    if (result.duplicate) {
      entry.markDuplicate();
      filesById.delete(entryId);
      return;
    }
    if (result.error || !result.billId) {
      entry.markError(result.error || 'Upload failed. Try this file again.');
      return;
    }
    entry.markLanded(result.billId);

    // Registering the bill in the session store is what lets the details view preview the file
    // without a round trip, and what the deep link and the table's own row share. No property is
    // sent with it: the pipeline matches the bill to one from its service address, and a bill it
    // cannot place lands in review rather than being guessed at here.
    const file = filesById.get(entryId);
    getParent(store).uploadedBills.addUploadedBill({
      billId: result.billId,
      fileName: result.fileName || entry.fileName,
      fileUrl: file ? URL.createObjectURL(file) : '',
      fileSizeMB: entry.fileSizeMB
    });
    filesById.delete(entryId);
  },
  failEntry(entryId, message) {
    var _store$entries$find2;
    (_store$entries$find2 = store.entries.find(item => item.id === entryId)) === null || _store$entries$find2 === void 0 || _store$entries$find2.markError(message);
  },
  /**
   * Submits everything picked so far and starts draining.
   *
   * A drain already running is left alone rather than restarted: re-running would cancel the
   * requests in flight, because the async model cancels the previous call of the same action.
   * It picks the newly submitted files up on its next pass instead.
   */
  startProcessing() {
    if (store.queuedCount === 0) return undefined;
    store.entries.filter(entry => entry.isEditable).forEach(entry => entry.markReady());
    // Nothing awaits this in the UI — that is the point, the modal is free to close. It is
    // returned so a test can wait for the drain it started.
    if (store.processQueueAsync.inProgress) return undefined;
    return store.processQueueAsync.run(undefined, []);
  },
  /** Drops what has finished and keeps what has not, which is what closing the modal leaves behind. */
  clearSettledEntries() {
    store.entries.filter(entry => entry.isSettled).forEach(entry => {
      filesById.delete(entry.id);
      store.entries.remove(entry);
    });
  },
  /**
   * Takes out everything that has not been sent yet and forgets what has finished. A file already
   * on its way is left alone: the request cannot be unsent, and a bill created by it would
   * otherwise be one nothing in the UI ever mentions again.
   */
  cancelQueue() {
    store.entries.filter(entry => entry.isEditable || entry.isReadyToSend || entry.isSettled).forEach(entry => {
      filesById.delete(entry.id);
      store.entries.remove(entry);
    });
  }
}));
export const bulkUpload = types.optional(BulkUploadStore, {});